import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm, 
  Tag,
  Space,
  Card,
  Upload,
  Switch,
  Typography
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  UploadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { Checkbox } from 'antd';
import axios from 'axios';

function ProxyManagement() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [checkingProxies, setCheckingProxies] = useState(false);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deletingProxies, setDeletingProxies] = useState(new Set());

  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchProxies();
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  const fetchProxies = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/proxies/?skip=${(page - 1) * pageSize}&limit=${pageSize}`);
      
      if (response.data && typeof response.data === 'object' && 'data' in response.data) {
        // Paginated response
        setProxies(response.data.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.total
        }));
      } else {
        // Non-paginated response (fallback)
        setProxies(response.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          pageSize: pageSize,
          total: response.data.length
        }));
      }
      
      // Clear selection when data changes (pagination)
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('Failed to fetch proxies');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  // Validate proxy URL format
  const validateProxyUrl = (_, value) => {
    if (!value) {
      return Promise.reject(new Error('Proxy URL is required'));
    }
    
    // Remove http:// prefix if present
    const cleanUrl = value.replace('http://', '').replace('https://', '');
    const parts = cleanUrl.split(':');
    
    if (parts.length === 4) {
      // Standard format: ip:port:username:password
      return Promise.resolve();
    } else if (parts.length === 2) {
      // Simple format: ip:port
      return Promise.resolve();
    } else {
      return Promise.reject(new Error('Invalid format. Use: ip:port:username:password or ip:port'));
    }
  };

  const handleDelete = async (proxyId) => {
    try {
      setDeletingProxies(prev => new Set(prev).add(proxyId));
      await axios.delete(`/api/proxies/${proxyId}`);
      message.success('Proxy deleted successfully');
      await fetchProxies();
    } catch (error) {
      message.error('Failed to delete proxy');
    } finally {
      setDeletingProxies(prev => {
        const next = new Set(prev);
        next.delete(proxyId);
        return next;
      });
    }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select proxies to delete');
      return;
    }

    try {
      // Delete each proxy
      const deletePromises = selectedRowKeys.map(proxyId => 
        axios.delete(`/api/proxies/${proxyId}`)
      );
      
      const results = await Promise.allSettled(deletePromises);
      const successful = results.filter(result => result.status === 'fulfilled').length;
      const failed = results.filter(result => result.status === 'rejected').length;

      if (successful > 0) {
        message.success(`Successfully deleted ${successful} proxy(ies)`);
        setSelectedRowKeys([]);
        await fetchProxies();
      }

      if (failed > 0) {
        message.warning(`${failed} proxy(ies) failed to delete`);
      }
    } catch (error) {
      message.error('Failed to delete selected proxies');
    }
  };

  const handleSelectAll = () => {
    const currentPageIds = (proxies || []).map(p => p.id);
    setSelectedRowKeys(currentPageIds);
  };

  const handleUnselectAll = () => {
    setSelectedRowKeys([]);
  };

  const handleToggleSelectAll = (checked) => {
    if (checked) {
      handleSelectAll();
    } else {
      handleUnselectAll();
    }
  };

  const isAllSelected = () => {
    const currentPageIds = (proxies || []).map(p => p.id);
    if (currentPageIds.length === 0) return false;
    return currentPageIds.every(id => selectedRowKeys.includes(id));
  };

  const isIndeterminate = () => {
    const currentPageIds = (proxies || []).map(p => p.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRowKeys.includes(id));
    return selectedOnPage.length > 0 && selectedOnPage.length < currentPageIds.length;
  };

  const handleToggleStatus = async (proxyId, isActive) => {
    try {
      await axios.put(`/api/proxies/${proxyId}`, { is_active: isActive });
      message.success(`Proxy ${isActive ? 'activated' : 'deactivated'}`);
      fetchProxies();
    } catch (error) {
      message.error('Failed to update proxy status');
    }
  };

  const handleSubmit = async (values) => {
    try {
      await axios.post('/api/proxies/', values);
      message.success('Proxy created successfully');
      setModalVisible(false);
      fetchProxies();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleBatchAdd = () => {
    batchForm.resetFields();
    setBatchModalVisible(true);
  };

  const handleBatchSubmit = async (values) => {
    try {
      const proxyList = values.proxy_list
        .split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      if (proxyList.length === 0) {
        message.error('Please enter at least one proxy');
        return;
      }

      const response = await axios.post('/api/proxies/batch-create', {
        proxies: proxyList
      });
      
      const { created_count, skipped_count, total_provided, errors } = response.data;
      
      let messageText = `Successfully created ${created_count} proxies`;
      if (skipped_count > 0) {
        messageText += `, skipped ${skipped_count} duplicates`;
      }
      if (errors && errors.length > 0) {
        messageText += `, ${errors.length} errors`;
        console.log('Errors:', errors);
      }
      
      message.success(messageText);
      setBatchModalVisible(false);
      fetchProxies();
    } catch (error) {
      message.error(error.response?.data?.detail || 'Failed to create proxies');
    }
  };

  const handleCheckProxy = async (proxyId) => {
    try {
      const response = await axios.post(`/api/proxies/${proxyId}/check`);
      const { status, response_time, ip_address } = response.data;
      
      if (status === 'live') {
        message.success(`Proxy is LIVE - IP: ${ip_address}, Response time: ${response_time}ms`);
      } else {
        message.error('Proxy is DEAD');
      }
      
      // Refresh proxy list to update status
      fetchProxies();
    } catch (error) {
      message.error('Failed to check proxy');
    }
  };

  const handleCheckAllProxies = async () => {
    try {
      setCheckingProxies(true);
      const response = await axios.post('/api/proxies/check-all');
      const { results } = response.data;
      
      const liveCount = results.filter(r => r.status === 'live').length;
      const deadCount = results.filter(r => r.status === 'dead').length;
      
      message.success(`Checked all proxies: ${liveCount} live, ${deadCount} dead`);
      fetchProxies();
    } catch (error) {
      message.error('Failed to check all proxies');
    } finally {
      setCheckingProxies(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: 'Proxy URL',
      dataIndex: 'proxy_url',
      key: 'proxy_url',
      ellipsis: true,
      render: (url) => {
        // Hide password in proxy URL for security
        if (url && url.includes(':')) {
          // Remove http:// prefix if present
          const cleanUrl = url.replace('http://', '').replace('https://', '');
          const parts = cleanUrl.split(':');
          if (parts.length === 4) {
            // Standard format: ip:port:username:password -> display as ip:port:username:***
            return `${parts[0]}:${parts[1]}:${parts[2]}:***`;
          } else if (parts.length === 2) {
            // Simple format: ip:port
            return url;
          }
        }
        return url;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggleStatus(record.id, checked)}
          checkedChildren={<CheckCircleOutlined />}
          unCheckedChildren={<CloseCircleOutlined />}
        />
      ),
    },
    {
      title: 'Proxy Status',
      dataIndex: 'proxy_status',
      key: 'proxy_status',
      render: (status, record) => {
        if (status === 'live') {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>LIVE</span>;
        } else if (status === 'dead') {
          return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>DEAD</span>;
        } else {
          return <span style={{ color: '#d9d9d9' }}>UNCHECKED</span>;
        }
      },
    },
    {
      title: 'Response Time',
      dataIndex: 'response_time',
      key: 'response_time',
      render: (time) => time ? `${time}ms` : '-',
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (ip) => ip || '-',
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleCheckProxy(record.id)}
            title="Check Proxy"
          >
            Check
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this proxy?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="primary"
              danger
              size="small"
              icon={<DeleteOutlined />}
              loading={deletingProxies.has(record.id)}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Proxy Management</h1>
        <p>Manage proxy servers for UnrealSpeech API requests</p>
      </div>

      <Card>
        <div className="action-buttons">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            className="action-btn"
          >
            Add Proxy
          </Button>
          <Button
            icon={<PlusOutlined />}
            onClick={handleBatchAdd}
            className="action-btn"
            type="default"
          >
            Add Proxies
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchProxies}
            className="action-btn"
          >
            Refresh
          </Button>
          <Button
            icon={<ThunderboltOutlined />}
            onClick={handleCheckAllProxies}
            loading={checkingProxies}
            className="action-btn"
            type="default"
          >
            Check All Proxies
          </Button>
        </div>

        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <Checkbox
            checked={isAllSelected()}
            indeterminate={isIndeterminate()}
            onChange={(e) => handleToggleSelectAll(e.target.checked)}
            style={{ marginRight: '16px' }}
          >
            Select All
          </Checkbox>
          <Popconfirm
            title={`Are you sure you want to delete ${selectedRowKeys.length} selected proxy(ies)?`}
            description="This action cannot be undone."
            onConfirm={handleBatchDelete}
            okText="Yes"
            cancelText="No"
            disabled={selectedRowKeys.length === 0}
          >
            <Button
              icon={<DeleteOutlined />}
              className="action-btn"
              danger
              disabled={selectedRowKeys.length === 0}
              style={{ marginRight: '16px' }}
            >
              Delete ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
          <div style={{ marginRight: '10px', color: '#666' }}>
            Selected: {selectedRowKeys.length}
          </div>
        </div>

        <div className="table-responsive">
          <Table
            columns={columns}
            dataSource={proxies || []}
            rowKey="id"
            loading={loading}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => {
                setSelectedRowKeys(keys);
              },
            }}
            scroll={{ x: 700 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              pageSizeOptions: ['10', '20', '50', '100', '200', '500', '1000'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
                fetchProxies(page, pageSize);
              },
              onShowSizeChange: (current, size) => {
                setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                fetchProxies(1, size);
              }
            }}
          />
        </div>
      </Card>

      <Modal
        title="Add Proxy"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="proxy_url"
            label="Proxy URL"
            rules={[
              { required: true, message: 'Please input proxy URL!' },
              { validator: validateProxyUrl }
            ]}
          >
            <Input placeholder="ip:port:username:password or ip:port" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Create
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add Multiple Proxies"
        open={batchModalVisible}
        onCancel={() => setBatchModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchSubmit}
        >
          <Typography.Paragraph>
            Enter multiple proxy URLs, one per line. Supported formats:
          </Typography.Paragraph>
          <Typography.Paragraph code>
            ip:port:username:password<br/>
            ip:port<br/>
            http://ip:port:username:password<br/>
            socks5://ip:port
          </Typography.Paragraph>
          
          <Form.Item
            name="proxy_list"
            label="Proxy List"
            rules={[
              { required: true, message: 'Please enter proxy list!' }
            ]}
          >
            <Input.TextArea
              rows={10}
              placeholder={`192.168.1.1:8080:user:pass
192.168.1.2:8080:user2:pass2
192.168.1.3:8080
http://192.168.1.4:8080:user3:pass3
socks5://192.168.1.5:1080`}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Add Proxies
              </Button>
              <Button onClick={() => setBatchModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default ProxyManagement;